import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/data/site";

const links = [
    { to: "/", label: "Index" },
    { to: "/projects", label: "Projects" },
    { to: "/about", label: "About" },
    { to: "/amenities", label: "Amenities" },
    { to: "/gallery", label: "Gallery" },
    { to: "/journal", label: "Journal" },
    { to: "/careers", label: "Careers" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    return (
        <>
            <header
                data-testid="site-navbar"
                className={`fixed inset-x-0 top-0 z-50 transition-[background,backdrop-filter,border] duration-500 ${
                    scrolled
                        ? "backdrop-blur-xl bg-forest-900/70 border-b border-white/10"
                        : "bg-transparent border-b border-transparent"
                }`}
            >
                <div className="mx-auto flex h-28 max-w-[1440px] items-center justify-between px-6 md:h-32 md:px-10">
                    <Link
                        to="/"
                        data-testid="nav-logo-link"
                        className="flex items-center group"
                    >
                        <div className="h-20 w-auto md:h-40 flex items-center justify-center transition-transform duration-500 group-hover:scale-[1.03]">
                            <img
                                src={LOGO_URL}
                                alt="KLiving"
                                className="h-full w-auto object-contain logo-white"
                            />
                        </div>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-7 xl:gap-8">
                        {[
                            { to: "/", l: "Home" },
                            { to: "/projects", l: "Projects" },
                            { to: "/about", l: "About" },
                            { to: "/amenities", l: "Amenities" },
                            { to: "/gallery", l: "Gallery" },
                            { to: "/journal", l: "Journal" },
                        ].map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`nav-link-${l.l.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `hover-underline font-mono-mini text-[11px] tracking-[0.28em] uppercase transition-colors ${
                                        isActive ? "text-lime" : "text-ivory/80 hover:text-ivory"
                                    }`
                                }
                            >
                                {l.l}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-4">
                        <Link
                            to="/contact"
                            data-testid="nav-contact-cta"
                            className="hidden md:inline-flex items-center gap-2 font-mono-mini text-[11px] tracking-[0.28em] uppercase text-forest-900 bg-lime px-5 py-3 hover:bg-lime-hi transition-colors"
                        >
                            Book a visit
                            <span aria-hidden>→</span>
                        </Link>
                        <button
                            data-testid="nav-menu-toggle"
                            onClick={() => setOpen((v) => !v)}
                            className="relative h-11 w-11 flex items-center justify-center border border-ivory/15 text-ivory hover:border-lime hover:text-lime transition-colors"
                            aria-label="Menu"
                        >
                            {open ? <X size={18} /> : <Menu size={18} />}
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        key="menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        data-testid="mobile-menu-overlay"
                        className="fixed inset-0 z-40 bg-forest-950/95 backdrop-blur-2xl"
                    >
                        <div className="mx-auto max-w-[1440px] h-full px-6 md:px-10 pt-28 md:pt-32 grid grid-cols-1 md:grid-cols-12 gap-10">
                            <div className="md:col-span-8">
                                <div className="font-mono-mini text-[10px] tracking-[0.35em] uppercase text-lime/80 mb-8">
                                    Navigate — 08 pages
                                </div>
                                <ul className="space-y-2">
                                    {links.map((l, i) => (
                                        <motion.li
                                            key={l.to}
                                            initial={{ y: 40, opacity: 0 }}
                                            animate={{ y: 0, opacity: 1 }}
                                            transition={{ duration: 0.6, delay: 0.05 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                                        >
                                            <NavLink
                                                to={l.to}
                                                data-testid={`menu-link-${l.label.toLowerCase()}`}
                                                className={({ isActive }) =>
                                                    `group flex items-baseline gap-6 py-3 border-b border-white/10 ${
                                                        isActive ? "text-lime" : "text-ivory"
                                                    }`
                                                }
                                            >
                                                <span className="font-mono-mini text-[10px] tracking-[0.32em] text-ivory/40 group-hover:text-lime transition-colors w-8">
                                                    0{i + 1}
                                                </span>
                                                <span className="font-serif-display text-[2.4rem] md:text-[4rem] leading-[0.95] tracking-tight group-hover:text-lime transition-colors">
                                                    {l.label}
                                                </span>
                                            </NavLink>
                                        </motion.li>
                                    ))}
                                </ul>
                            </div>
                            <div className="md:col-span-4 flex flex-col justify-end pb-14">
                                <div className="font-mono-mini text-[10px] tracking-[0.32em] uppercase text-ivory/40 mb-4">
                                    Studio
                                </div>
                                <div className="font-serif-display italic text-ivory/85 text-lg leading-snug mb-6">
                                    “We build slowly. We build once.”
                                </div>
                                <div className="text-ivory/60 text-sm leading-relaxed">
                                    MGM Heights, Plot 140/9957<br />
                                    K Living — 3rd floor, Kalinga Nagar<br />
                                    Bhubaneswar 751003, Odisha<br />
                                    <a href="mailto:hello@kliving.co" className="hover-underline text-ivory/80">hello@kliving.co</a>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
